import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import ListItemButton from "@mui/material/ListItemButton";

/**
 * Skip-to-content link for keyboard accessibility (WCAG 2.4.1)
 * Hidden visually until focused, then appears at top of page
 */
export const SkipLink = styled("a")(({ theme }) => ({
  position: "absolute",
  top: -100,
  left: 16,
  zIndex: 9999,
  padding: theme.spacing(1.5, 2),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  textDecoration: "none",
  fontWeight: 600,
  borderRadius: theme.shape.borderRadius,
  "&:focus": {
    top: 16,
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

export const HEADER_HEIGHT = 80;
export const ACCENT_BAR_HEIGHT = 3;
export const SIDEBAR_WIDTH = 240;
export const BOTTOM_NAV_HEIGHT = 64;

export const LayoutRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  backgroundColor: theme.palette.background.default,
}));

export const Header = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  height: HEADER_HEIGHT,
  padding: theme.spacing(0, 3),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
}));

export const AccentBar = styled(Box)(({ theme }) => ({
  height: ACCENT_BAR_HEIGHT,
  width: "100%",
  background: `linear-gradient(90deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.light} 50%, ${theme.palette.secondary.main} 100%)`,
}));

export const MainContainer = styled(Box)({
  display: "flex",
  flex: 1,
});

export const Sidebar = styled(Box)(({ theme }) => ({
  width: SIDEBAR_WIDTH,
  backgroundColor: theme.palette.background.default,
  borderRight: `1px solid ${theme.palette.divider}`,
  minHeight: `calc(100vh - ${HEADER_HEIGHT + ACCENT_BAR_HEIGHT}px)`,
  display: "flex",
  flexDirection: "column",
  // Hide sidebar on mobile (bottom nav replaces it)
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

export const NavList = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
}));

export const NavItem = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== "active",
})<{ active?: boolean }>(({ theme, active }) => ({
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 2),
  gap: theme.spacing(1.5),
  ...(active && {
    backgroundColor: `${theme.palette.primary.main}14`, // 8% opacity
    color: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: `${theme.palette.primary.main}1F`, // 12% opacity
    },
  }),
  ...(!active && {
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  }),
}));

export const NavDivider = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 2),
  marginTop: theme.spacing(2),
  paddingTop: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

export const NavSectionLabel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 2, 1, 2),
  fontSize: "0.75rem",
  fontWeight: 600,
  color: theme.palette.text.disabled,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
}));

export const MainContent = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: "auto",
  // Responsive padding: 16px on mobile, 24px on tablet, 32px on desktop
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(3),
  },
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(4),
  },
  // Add bottom padding on mobile to account for bottom navigation
  [theme.breakpoints.down("md")]: {
    paddingBottom: `calc(${theme.spacing(2)} + ${BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px))`,
  },
}));

// Co-branded header styles
export const BrandingContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
}));

export const AppLogo = styled("img")({
  height: 48,
  width: "auto",
  objectFit: "contain",
});

export const TenantLogo = styled("img")({
  height: 40,
  width: "auto",
  objectFit: "contain",
});

export const BrandDivider = styled(Box)(({ theme }) => ({
  width: 1,
  height: 32,
  backgroundColor: "rgba(255, 255, 255, 0.3)",
  margin: theme.spacing(0, 1),
}));
